import React, { createContext, useContext, useMemo, ReactNode } from 'react'

import { isTestingModeEnabled } from '@/utils/featureFlags'

type FeatureFlagsContextValue = {
  isTesting: boolean
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined
)

type FeatureFlagsProviderProps = {
  children: ReactNode
}

export const FeatureFlagsProvider = React.memo(
  ({ children }: FeatureFlagsProviderProps) => {
    const isTesting = isTestingModeEnabled()

    const value = useMemo(
      () => ({
        isTesting,
      }),
      [isTesting]
    )

    return (
      <FeatureFlagsContext.Provider value={value}>
        {children}
      </FeatureFlagsContext.Provider>
    )
  }
)

FeatureFlagsProvider.displayName = 'FeatureFlagsProvider'

/**
 * Hook to access feature flags context
 * @throws Error if used outside of FeatureFlagsProvider
 */
export const useFeatureFlags = (): FeatureFlagsContextValue => {
  const context = useContext(FeatureFlagsContext)

  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider'
    )
  }

  return context
}
